import React from 'react';
import {
  AppErrorCode,
  UserErrorCode,
  useSelfOnboardingWorkerCreateMutation,
  useUserUpdateIdentityMutation,
  useUpdateWorkerMutation,
  DocumentKey,
  useUpsertJobsiteWorkerDocumentMutation,
  SelfOnboardingWorkerAction,
  useAssignContractorWorkerToJobsiteMutation,
  SelfOnboardingWorkerInfo,
  SelfOnboardingAbortReason,
} from 'apollo/generated/client-operations';
import { WorkerAlreadyExistsErrorExtensions } from 'types';
import { Form, FormOnSubmit } from 'components/form';
import { AlertService } from 'components/alertService';
import { getGraphQLError, getGraphQLErrorByCode } from 'utils/error';
import { AlreadyExistingItem } from 'components/alreadyExistingItem';
import { AuthContext } from 'auth';
import { useFlags } from 'featureFlags';
import { isNotEmpty } from 'utils';
import { commonClasses } from 'containers/selfOnboarding/steps/common.style';
import {
  AcknowledgmentStatus,
  SelfOnboardingBasicInfoFormData,
  SelfOnboardingStepKey,
  SelfOnboardingStepProps,
} from 'containers/selfOnboarding/steps/types';
import { Copy, languageOptions } from 'containers/selfOnboarding/steps/utils';
import { getPrettyFormattedUtcDate } from 'utils/dates';
import { primaryLanguageOptions as primaryLanguageOptionsValues } from 'utils/constants';
import {
  StepActions,
  StepInfo,
  StepInfoTitle,
  StepJobsiteInfo,
} from 'containers/selfOnboarding/steps/components';
import { isDocumentVisible } from 'containers/selfOnboarding/helpers/utils';
import { useSelfOnboardingFormCommonProps } from 'containers/selfOnboarding/helpers/forms';
import { useGetIsSelfOnboardingClosed } from 'containers/selfOnboarding/helpers/useGetIsSelfOnboardingClosed';
import {
  getFormInputsHook,
  getDefaultValues,
  getCreateInput,
  getUpdateInputs,
  getEditableFields,
  getWorkerConsentDocumentInput,
  getWorkerAlreadyExistsNextStep,
} from './BasicInfoStep.forms';

export function BasicInfoStep(
  props: SelfOnboardingStepProps,
): React.ReactElement {
  const {
    state,
    updateState,
    navigation,
    stepConfig,
    localize,
    language,
    jobsiteInvitation,
    refetchData,
  } = props;
  const {
    basicInfo,
    worker,
    jobsiteWorker,
    userAccountId,
    contractorId,
    verificationPhoneNumber,
  } = state;
  const { workerConsentStatus } = basicInfo ?? {};
  const { workerId } = worker ?? {};
  const {
    getNextStep,
    goToNextStep,
    goToStep,
    state: navigationState,
  } = navigation;
  const { isReview, autoFocusField } = navigationState;

  const { authState } = React.useContext(AuthContext);
  const { authenticatedSelfOnboardingEnabled, consentRecaptureGateEnabled } =
    useFlags();
  const [isSaving, setIsSaving] = React.useState(false);

  const [selfOnboardingWorkerCreate] = useSelfOnboardingWorkerCreateMutation();
  const [assignContractorWorkerToJobsite] =
    useAssignContractorWorkerToJobsiteMutation();
  const [updateUserIdentity] = useUserUpdateIdentityMutation();
  const [updateWorker] = useUpdateWorkerMutation();
  const [upsertJobsiteWorkerDocument] =
    useUpsertJobsiteWorkerDocumentMutation();
  const { isSelfOnboardingClosed } = useGetIsSelfOnboardingClosed();

  const languageName = (
    languageOptions.find((opt) => opt.value === language) ?? languageOptions[0]
  ).label;
  const primaryLanguage = primaryLanguageOptionsValues.find((optValue) =>
    languageName.startsWith(optValue),
  );

  const { jobsite, contractor } = jobsiteInvitation?.jobsiteContractor ?? {};
  const { modules, jobsiteId } = jobsite ?? {};
  const isWorkerConsentAvailable = isDocumentVisible(
    DocumentKey.WorkerConsentDocument,
    modules,
  );
  const isWorkerConsentConfirmed = !!workerConsentStatus?.includes(
    AcknowledgmentStatus.Confirmed,
  );

  const initialIsContinueActionEnabled =
    !isWorkerConsentAvailable || isWorkerConsentConfirmed;
  const [isContinueActionEnabled, setIsContinueActionEnabled] = React.useState(
    initialIsContinueActionEnabled,
  );

  const onSubmit: FormOnSubmit<SelfOnboardingBasicInfoFormData> = async (
    data,
    event,
    dirtyFields,
    formApi,
  ): Promise<void> => {
    if (isSaving) return;
    setIsSaving(true);

    try {
      if (workerId) {
        if (await isSelfOnboardingClosed()) {
          goToStep(SelfOnboardingStepKey.Closed);
          return;
        }

        // update existing worker
        const { userIdentityInput, workerInput } =
          getUpdateInputs(data, dirtyFields) ?? {};

        if (userIdentityInput) {
          await updateUserIdentity({
            variables: { input: { userAccountId, userIdentityInput } },
          });
        }

        if (workerInput) {
          await updateWorker({ variables: { workerId, workerInput } });
        }

        if (!jobsiteWorker) {
          // assign worker to the jobsite and contractor
          const response = await assignContractorWorkerToJobsite({
            variables: {
              input: {
                isSelfOnboarding: true,
                workerId,
                jobsiteId,
                contractorId: contractorId?.value ?? contractor?.contractorId,
                jobsiteWorkerInput: {},
              },
            },
          });

          const { jobsiteWorker: newJobsiteWorker } =
            response.data.assignContractorWorkerToJobsite;
          const { isAssignedToMultipleJobsites } =
            newJobsiteWorker.contractorWorker.worker;
          updateState({
            jobsiteWorker: newJobsiteWorker,
            worker: { ...worker, isAssignedToMultipleJobsites },
          });
        }

        if (isNotEmpty(dirtyFields)) {
          // Merge (not replace): in the authenticated flow the name fields are
          // hidden on this step, so they're filtered out of the form inputs and
          // absent from `data`. Replacing would wipe the name captured earlier
          // (account-creation step), blanking it on the review screen.
          updateState({ basicInfo: { ...basicInfo, ...data } });
        }
      } else if (
        isNotEmpty(dirtyFields) ||
        authenticatedSelfOnboardingEnabled
      ) {
        // Authenticated flow: the name is staged on the WorkerName step, which
        // precedes this one. If a phone-verified user URL-jumped straight to
        // Basic Info and skipped it, the staged name is missing — send them back
        // rather than creating an account with an empty first/last name.
        if (
          authenticatedSelfOnboardingEnabled &&
          (!basicInfo?.firstName || !basicInfo?.lastName)
        ) {
          setIsSaving(false);
          goToStep(SelfOnboardingStepKey.WorkerName);
          return;
        }

        // create a worker
        const nextStep = getNextStep();
        const input = getCreateInput({
          nextStep,
          contractorId: contractorId?.value,
          jobsiteInvitation,
          primaryLanguage,
          data,
          dirtyFields,
          // Name lives on the WorkerName step in the authenticated flow; pull the
          // staged values from client state since they aren't on this form.
          stagedName: authenticatedSelfOnboardingEnabled
            ? {
                firstName: basicInfo?.firstName,
                middleInitial: basicInfo?.middleInitial,
                lastName: basicInfo?.lastName,
                suffix: basicInfo?.suffix,
              }
            : undefined,
        });

        // When the worker exists already, and is assigned to `Contractor Unassigned` contractor,
        // `selfOnboardingWorkerCreate` mutation will return error with `continueAction` set to `AbortOnboarding`.
        // In this case, we might want to update the worker's contractorId (which is `Contractor Unassigned`)
        // to the selected contractor.
        // We should probably pass a flag to the mutation to indicate that we want to change the default contractor
        // with the selected one.

        const { data: resultData } = await selfOnboardingWorkerCreate({
          variables: {
            input: {
              ...input,
              // Set in the authenticated self-onboarding flow (from the OTP
              // step); the backend links the new account to that FusionAuth user
              // and ignores it otherwise.
              phoneVerificationToken: state.phoneVerificationToken,
            },
          },
        });
        const { session, user } = resultData.selfOnboardingWorkerCreate.auth;
        authState.signIn(session.sessionToken, null);

        if (isWorkerConsentAvailable) {
          const { jobsiteWorkerId } =
            resultData.selfOnboardingWorkerCreate.jobsiteWorker;
          const documentInput = await getWorkerConsentDocumentInput({
            user,
            jobsiteInvitation,
            state,
            jobsiteWorkerId,
          });
          if (documentInput) {
            await upsertJobsiteWorkerDocument({
              variables: { input: documentInput },
            });
          }
        }

        await refetchData();
      }

      setIsSaving(false);
      goToNextStep();
    } catch (error) {
      event.preventDefault();
      setIsSaving(false);

      const workerAlreadyExistsError =
        getGraphQLErrorByCode<WorkerAlreadyExistsErrorExtensions>(
          error,
          AppErrorCode.WorkerAlreadyExists,
        );

      if (workerAlreadyExistsError) {
        const { workerInfo } = workerAlreadyExistsError.extensions ?? {};
        const [matchedWorker] = workerInfo ?? [];
        const { selfOnboardingInfo, phone, email, ssnLastFour, birthDate } =
          matchedWorker ?? {};

        updateState({
          matchedWorker,
          basicInfo: {
            ...data,
            // don't clobber staged name w/ empty values from ...data above if flag is on
            ...(authenticatedSelfOnboardingEnabled && {
              firstName: basicInfo?.firstName,
              middleInitial: basicInfo?.middleInitial,
              lastName: basicInfo?.lastName,
              suffix: basicInfo?.suffix,
            }),
            phoneNumber: phone || data.phoneNumber,
            email: email || data.email,
            birthDate: getPrettyFormattedUtcDate(birthDate) || data.birthDate,
            ssnLastFour: ssnLastFour || data.ssnLastFour,
          },
          didWorkerMatch: true,
        });

        const nextStep = getWorkerAlreadyExistsNextStep({
          selfOnboardingInfo,
          authenticatedSelfOnboardingEnabled,
        });
        goToStep(nextStep);

        return;
      }

      const contractorWorkerToJobsiteAssignmentError =
        getGraphQLErrorByCode<SelfOnboardingWorkerInfo>(
          error,
          'BAD_USER_INPUT',
        );
      if (contractorWorkerToJobsiteAssignmentError) {
        const { continueAction, abortReason } =
          contractorWorkerToJobsiteAssignmentError.extensions ?? {};
        if (continueAction === SelfOnboardingWorkerAction.AbortOnboarding) {
          const abortStep =
            abortReason ===
            SelfOnboardingAbortReason.JobsiteWorkerWithDifferentContractor
              ? SelfOnboardingStepKey.JobsiteWorkerWithDifferentContractor
              : SelfOnboardingStepKey.AlreadyCompleted;
          goToStep(abortStep);
          return;
        }
      }

      const emailAlreadyExistsError = getGraphQLErrorByCode(
        error,
        UserErrorCode.EmailAlreadyExists,
      );
      if (emailAlreadyExistsError) {
        formApi.setError('email', {
          message: <AlreadyExistingItem itemType="Email" />,
          shouldFocus: true,
        });
        return;
      }

      const phoneAlreadyExistsError = getGraphQLErrorByCode(
        error,
        UserErrorCode.PhoneAlreadyExists,
      );
      if (phoneAlreadyExistsError) {
        formApi.setError('phoneNumber', {
          message: <AlreadyExistingItem itemType="Phone" />,
          shouldFocus: true,
        });
        return;
      }
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(error),
      );
    }
  };

  // `changedValues` will be applied to the form if worker has not been created yet.
  // This could happen if a worker is matched and then user gets back to this page
  // in order to change some values. In this situation, the previously changed fields
  // need to be dirty in order to be sent again to the mutation.
  const { defaultValues, changedValues, editableFields } = React.useMemo(() => {
    // `defaultValues` is initialized as empty object if worker doesn't exist
    const formDefaultValues = getDefaultValues(worker ? basicInfo : null);

    // set `changedValues` only if `basicInfo` has been previously changed
    // and worker is null, which means that the query has completed (otherwise worker would be undefined)
    const formChangedValues =
      basicInfo && worker === null ? getDefaultValues(basicInfo) : null;

    const values = formChangedValues ?? formDefaultValues;

    return {
      editableFields: getEditableFields({
        worker,
        values,
        verificationPhoneNumber,
        authenticatedSelfOnboardingEnabled,
      }),
      defaultValues: formDefaultValues,
      changedValues: formChangedValues,
    };
  }, [
    basicInfo,
    worker,
    jobsiteInvitation,
    verificationPhoneNumber,
    authenticatedSelfOnboardingEnabled,
  ]);

  const inputs = getFormInputsHook({
    jobsiteInvitation,
    navigation,
    state,
    updateState,
    localize,
    editableFields,
    isWorkerConsentAvailable,
    isWorkerConsentConfirmed,
    setIsContinueActionEnabled,
    authenticatedSelfOnboardingEnabled,
  });

  const { fieldsConfig, localization } = useSelfOnboardingFormCommonProps(
    stepConfig,
    localize,
  );

  // Show the SMS opt-in disclosure only when the phone field is collected on
  // this screen, i.e. not hidden by the jobsite configuration. When the phone
  // field is hidden, keep the generic acknowledgement copy.
  const isPhoneVisible = !fieldsConfig.fields.phone?.isHidden;

  // The passive "by clicking Continue you agree to the Terms of Use and Privacy
  // Policy" acknowledgement is redundant in the authenticated flow once policy
  // consent is captured explicitly upstream (the account-creation checkbox +
  // the re-capture gate). Suppress it exactly when that explicit capture is
  // active — the auth flow and the consent gate are both on. The launch
  // policies are seeded by the stack's data migration, so those two flags alone
  // are a sufficient signal (no need to also check for seeded documents); the
  // legacy flow, which still relies on this acknowledgement, is untouched.
  const hasExplicitPolicyConsent =
    authenticatedSelfOnboardingEnabled && consentRecaptureGateEnabled;

  return (
    <div className={commonClasses.stepContainer}>
      <StepInfo>
        <StepInfoTitle
          title={`${localize(Copy.self_onboarding_worker_match_header)} 👋`}
          subtitle={localize(Copy.self_onboarding_worker_match_instructions)}
          withBottomBorder
          hideOnMobile
        />
        <StepJobsiteInfo
          jobsiteInvitation={jobsiteInvitation}
          jobsiteWorker={jobsiteWorker}
          contractorName={contractorId?.label}
        />
      </StepInfo>
      <Form
        inputs={inputs}
        defaultValues={defaultValues}
        changedValues={changedValues}
        onSubmit={onSubmit}
        autoFocus={
          (autoFocusField as keyof SelfOnboardingBasicInfoFormData) ?? true
        }
        renderBelow={
          <StepActions
            jobsiteInvitation={jobsiteInvitation}
            localize={localize}
            isReview={isReview}
            actions={isReview ? 'all' : 'continue'}
            onSkip={goToNextStep}
            continueActionWithSpinner={isSaving}
            continueActionEnabled={isContinueActionEnabled}
            showAcknowledgementText={!isReview && !hasExplicitPolicyConsent}
            showSmsOptInAcknowledgement={isPhoneVisible}
          />
        }
        validationTriggers={[language]}
        className={commonClasses.form}
        inputsContainerClassName={commonClasses.formInputsContainer}
        fieldsConfig={fieldsConfig}
        localization={localization}
      />
    </div>
  );
}
